<svelte:options tag='swc-address-email' immutable={true} />

<script>

  import { onMount } from 'svelte';
  import { cookieService } from './helpers/cookie-service';
  import { apiService } from './helpers/api-service';
  import { validationService } from './helpers/validation-service';
  import Select from './components/Select.svelte';
  import Input from './components/Input.svelte';
  import PrimaryButton from './components/PrimaryButton.svelte';
  import SecondaryButton from './components/SecondaryButton.svelte';
  import TermsAndConditions from './components/TermsAndConditions.svelte';
  import Loading from './components/LoadingSpinner.svelte';
  import ValidationMessage from './components/ValidationMessage.svelte';
  import Modal from './components/Modal.svelte';
  import FeefoRating from './components/FeefoRating.svelte';

  export let autofocus;
  export let startslug;
  export let appurl;
  export let apiurl;
  export let hidetitle;

  const manualAddressValue = 'manual';

  let hasInputsSet = false;
  let areAddressesLoading = false;
  let isLoading = true;

  // User input values
  let addressId = '';
  let postcode = '';
  let acceptedMarketingConsent = false;

  var manualAddressLine1 = '';
  var manualAddressLine2 = '';
  var manualAddressTown = '';

  let addressValidationMessage = null;
  let postcodeValidationMessage = null;
  let manualAddressLine1ValidationMessage = null;
  let manualTownValidationMessage = null;

  let energySignUp;

  let addresses = [];

  let submitError = null;
  let isPristine = true;
  let isDuplicatedModalVisible = false;

  const getAddressesForPostcode = async (postcodeToGet) => {
    if (postcodeValidationMessage) {
      addresses = [];
      addressId = '';
      return;
    }
    try {
      const retrievedAddresses = await apiService.getAddresses(postcodeToGet);
      addresses = [
        ...retrievedAddresses.addresses,
        { id: manualAddressValue, buildingName: 'Enter address manually' }
      ]
    } catch (error) {
      addresses = [];
    }
  };

  const initSignUp = async () => {
    try {
      energySignUp = await apiService.getSignUp();
    } catch (error) {}
    if (energySignUp) {
      const orderRequest = energySignUp.orderRequest || {};
      acceptedMarketingConsent = Boolean(
        orderRequest.marketingConsentAt
      );
      const address = await apiService.getAddressForSignUp(energySignUp);
      const isManualAddress = address && address.type === manualAddressValue;
      addressId = isManualAddress ? manualAddressValue : address && address.id;
      if (isManualAddress) {
        const manualAddressLine1Raw = [address.buildingName, address.street].filter(Boolean).join(" ");
        const manualAddressLine1Trimmed = manualAddressLine1Raw && manualAddressLine1Raw.trim();
        manualAddressLine1 = manualAddressLine1Trimmed
        manualAddressLine2 = address.subBuildingName;
        manualAddressTown = address.town;
      }
      postcode = address
        && address.postcode && address.postcode.toUpperCase();
      if (postcode) await getAddressesForPostcode(postcode);
    }
    isLoading = false;
  };

  const init = async () => {
    // Wait for properties to be initialised
    if (startslug === undefined) {
      setTimeout(() => init(), 30);
      return;
    }
    hasInputsSet = true;
    apiService.setBaseUrl(apiurl);
    await initSignUp();
  };
  onMount(() => init());

  const validatePostcode = async () => {
    postcodeValidationMessage = await validationService.validatePostcode(
      postcode,
      true
    );
    return postcodeValidationMessage;
  };

  const postcodeChanged = async (newPostcode) => {
    const newValue = newPostcode && newPostcode.toUpperCase();
    const valueChanged = postcode !== newValue;
    if (newValue && !valueChanged) return;
    postcode = newValue;
    areAddressesLoading = true;
    await validatePostcode();
    await getAddressesForPostcode(postcode);
    areAddressesLoading = false;
  };

  const validateAddress = () => {
    addressValidationMessage = (
      addressId === manualAddressValue
      || addresses.find(address => address.id === addressId)
    ) ? null : 'Please select an address';
    return addressValidationMessage;
  };

  const addressChanged = (newAddressId) => {
    addressId = newAddressId;
    validateAddress();
  };

  const validateManualAddressLine1 = () => {
    manualAddressLine1ValidationMessage =
      addressId === manualAddressValue
        ? validationService.validateManualAddressLine1(manualAddressLine1)
        : null;
    return manualAddressLine1ValidationMessage;
  };

  const manualAddressLine1Changed = (newManualAddressLine1) => {
    manualAddressLine1 = newManualAddressLine1;
    validateManualAddressLine1();
  };

  const validateManualAddressTown = () => {
    manualTownValidationMessage =
      addressId === manualAddressValue
      ? validationService.validateManualAddressTown(manualAddressTown)
      : null;
    return manualTownValidationMessage;
  };

  const manualAddressTownChanged = (newManualTown) => {
    manualAddressTown = newManualTown;
    validateManualAddressTown();
  };

  const closeModal = () => {
    isDuplicatedModalVisible = false;
  };

  const goToSignUpJourney = () => {
    window.location.href = `${appurl}${startslug}`;
  };

  const getAddressLabel = (address) => {
    const firstPart = [
      address.subBuildingName,
      address.buildingNumber,
      address.buildingName
    ].filter(Boolean).join(", ");
    return [firstPart, address.street].join(" ");
  };

  const onSubmit = async () => {
    isPristine = false;
    const validationFunctions = [
      validatePostcode,
      validateAddress,
      validateManualAddressLine1,
      validateManualAddressTown
    ];
    const invalidMessages = (await Promise.all(
      validationFunctions.map(async (func) => {
        const validationError = await func();
        return validationError;
      })
    ))
    if (invalidMessages.some(Boolean)) return;
    const isManual = addressId === manualAddressValue;

    const [firstPart, ...streetNameFull] = manualAddressLine1.split(" ");
    const hasMoreThanOnePart = streetNameFull.length > 0;
    const buildingName = hasMoreThanOnePart ? firstPart : null;
    const street = hasMoreThanOnePart ? streetNameFull.join(" ") : firstPart;

    const address = isManual
      ? {
        postcode,
        buildingName: buildingName || undefined,
        street: street,
        subBuildingName: manualAddressLine2 || undefined,
        town: manualAddressTown || undefined,
        type: manualAddressValue
      }
      : addresses.find(address => address.id === addressId);
    isLoading = true;
    try {
      const signUpId = energySignUp.id;
      isManual
        ? await apiService.saveManualAddressAndEmailPage(
          signUpId,
          address,
          undefined,
          acceptedMarketingConsent
        )
        : await apiService.saveAddressAndEmailPage(
          signUpId,
          address,
          undefined,
          acceptedMarketingConsent
        );

      const hasExistingAutoSwitchResponse = await apiService.hasExistingAutoSwitch(
        signUpId
      )
      if (hasExistingAutoSwitchResponse.hasExistingAutoSwitch) {
        isDuplicatedModalVisible = true;
      } else {
        goToSignUpJourney();
      }
    } catch (error) {
      console.log("Error", error);
      submitError = 'Our site is currently under maintenance. Please try again later.';
    }
    isLoading = false;
  };
</script>

<style>
  .main {
    display: flex;
    flex-direction: column;
    background: #FFFFFF;
    padding: 21px 20px 29.5px;
    border-radius: 10px;
    text-align: left;
    position: relative;
    overflow: hidden;
  }
  .title {
    color: #111155;
    font-size: 1.125rem;
    line-height: 4rem;
    margin: 0 0 4px;
    display: none;
  }
  swc-input, swc-select {
    margin-top: 10px;
  }
  swc-input:first-of-type {
    margin-top: 0;
  }
  swc-terms-and-conditions {
    margin-top: 12px;
  }
  .loading-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.5);
  }
  swc-primary-button, swc-secondary-button {
    margin-top: 12px;
  }
  swc-feefo-rating {
    margin-top: 14px;
  }
  swc-validation-message {
    display: flex;
    justify-content: center;
  }
  p {
    color: rgba(17, 17, 85, 0.7);
    line-height: 1.8;
  }
  .modal-btns {
    display: flex;
  }
  .modal-btns swc-secondary-button, .modal-btns swc-primary-button {
    width: 100%;
  }

  @media (min-width: 768px) {
    .main {
      padding: 9px 30px 29px;
    }
    .title {
      display: block;
    }
  }
</style>

{#if hasInputsSet}
  <div class='main'>
    {#if !hidetitle}
      <h3 class='title'>Find energy deals</h3>
    {/if}
    <swc-input
      autocomplete='postal-code'
      focusoninit={autofocus}
      placeholder='Postcode'
      value={postcode}
      data-e2e-id='postcode-input'
      on:update={(newPostcode) => postcodeChanged(newPostcode.detail)}
      validationmessage={postcodeValidationMessage}
      isformpristine={isPristine}
      hidevalidationonmobile={true} />

    <swc-select
      placeholder='Address'
      data-e2e-id='address-select'
      value={addressId}
      options={addresses.map(address => ({
        value: address.id,
        label: getAddressLabel(address)
      }))}
      on:update={(event) => addressChanged(event.detail)}
      validationmessage={addressValidationMessage}
      isloading={areAddressesLoading}
      isformpristine={isPristine}
      hidevalidationonmobile={true} />

    {#if addressId === manualAddressValue}
      <swc-input
        autocomplete='address-line1'
        placeholder='Address line 1'
        value={manualAddressLine1}
        data-e2e-id='address-line-1-input'
        on:update={(newAddressLine1) => manualAddressLine1Changed(newAddressLine1.detail)}
        validationmessage={manualAddressLine1ValidationMessage}
        isformpristine={isPristine}
        hidevalidationonmobile={true} />

      <swc-input
        autocomplete='address-line2'
        placeholder='Address line 2'
        value={manualAddressLine2}
        data-e2e-id='address-line-2-input'
        on:update={(newAddressLine2) => manualAddressLine2 = newAddressLine2.detail}
        isformpristine={isPristine}
        hidevalidationonmobile={true} />

      <swc-input
        autocomplete='address-level2'
        placeholder='Town'
        value={manualAddressTown}
        data-e2e-id='town-input'
        on:update={(newTown) => manualAddressTownChanged(newTown.detail)}
        validationmessage={manualTownValidationMessage}
        isformpristine={isPristine}
        hidevalidationonmobile={true} />
    {/if}

    <swc-terms-and-conditions />

    <swc-primary-button data-e2e-id='submit' on:press={onSubmit} text='Compare Energy' />
    <swc-feefo-rating></swc-feefo-rating>
    {#if isLoading}
      <div class='loading-wrapper'>
        <swc-loading-spinner />
      </div>
    {/if}
    {#if submitError}
      <swc-validation-message message={submitError} />
    {/if}

    {#if isDuplicatedModalVisible}
      <swc-modal on:close={closeModal}>
        <div slot='content'>
          <p>
            This address is already signed up to auto switching so you can get a new quote but
            you can't sign up again. Please
            <a href="https://apps.switchcraft.co.uk/auth/login">sign in</a>
            or <a href="https://www.switchcraft.co.uk/contact-us/">contact us</a>
            to update your account.
          </p>
          <div class='modal-btns'>
            <swc-secondary-button text='Change address' on:press={closeModal} />
            <swc-primary-button text='Continue' on:press={goToSignUpJourney} />
          </div>
        </div>
      </swc-modal>
    {/if}
  </div>
{/if}
